import type { StudioSystemType } from 'types/studio/items'
import { parseIntegrationJson } from 'util/misc'
import { handleApplyMountingSetting, saveInputs } from '../nativeMounting/Inputs'
import {
  ensureVanDerValkPitchedCladdingOptionsForType,
  isVanDerValkPitchedCladdingType,
} from './applyVanDerValkPitchedCladdingOptions'
import { getVanDerValkPitchedCladdingForRoofType } from './pitchedCladding'
import type { VanDerValkPitchedCladdingType } from './pitchedCladdingFieldConfig'
import { requestVanDerValkSuitabilityFetchIfNeeded } from './requestVanDerValkSuitabilityFetch'
import { getRoofName } from '../nativeMounting/utils'
import {
  applyVanDerValkDlubalFetchFallbacksIfUnset,
  getDlubalUKDistanceToSeaCache,
  getDlubalUKSnowCache,
  getDlubalUKSnowElevationCache,
  getDlubalUKWindCache,
  mapDlubalUKSnowZoneToVanDerValkIdx,
  requestAutoFetchDlubalUKDataIfNeeded,
} from '../nativeMounting/mounting_systems/ukDlubal'

function applyVanDerValkIntegrationFieldIfUnset(
  system: StudioSystemType,
  field: string,
  value: number
): void {
  if (!Number.isFinite(value)) {
    return
  }
  const vdv = (parseIntegrationJson(system.integration_json) as { vanDerValk?: Record<string, unknown> } | null)
    ?.vanDerValk
  const current = vdv?.[field]
  if (current !== undefined && current !== null && current !== '') {
    return
  }
  handleApplyMountingSetting(field, value, system.uuid, 'vanDerValk')
}

/** Mirror cached UK Dlubal values into Van der Valk integration_json when fields are still empty. */
export function applyVanDerValkDlubalUKFieldsFromCacheIfNeeded(system: StudioSystemType): void {
  if (!system || system.mounting !== 'vanDerValk') {
    return
  }

  const windCache = getDlubalUKWindCache()
  if (windCache?.success && windCache.value != null && Number.isFinite(windCache.value)) {
    applyVanDerValkIntegrationFieldIfUnset(system, 'windSpeedMs', windCache.value)
  }

  const snowCache = getDlubalUKSnowCache()
  if (snowCache?.success && snowCache.value != null && Number.isFinite(snowCache.value)) {
    applyVanDerValkIntegrationFieldIfUnset(
      system,
      'snowZone',
      mapDlubalUKSnowZoneToVanDerValkIdx(snowCache.value)
    )
  }

  const elevationCache = getDlubalUKSnowElevationCache()
  if (elevationCache.success && elevationCache.value != null && Number.isFinite(elevationCache.value)) {
    applyVanDerValkIntegrationFieldIfUnset(system, 'altitudeAboveSea', elevationCache.value)
  }

  const distanceCache = getDlubalUKDistanceToSeaCache()
  if (distanceCache.success && distanceCache.value != null && Number.isFinite(distanceCache.value)) {
    applyVanDerValkIntegrationFieldIfUnset(system, 'distanceToShorelineKM', distanceCache.value)
  }
}

/**
 * Kick off background auto-populate when Van der Valk mounting is selected (flat- or pitched-roof).
 * Seeds default inputs, fetches suitability, and fetches UK Dlubal weather fields without opening the tab.
 */
export function vanDerValkOnApply(system: StudioSystemType): void {
  if (!system || system.mounting !== 'vanDerValk') {
    return
  }

  if (window.projectForm) {
    try {
      saveInputs(window.projectForm, system)
    } catch (error) {
      console.warn('Error seeding Van der Valk default mounting options:', system.uuid, error)
    }
  }

  if (system.mounting_type === 'onRoof') {
    const roofTypeName = getRoofName(window.projectForm?.getState?.()?.values?.roof_type) ?? null
    const claddingType = getVanDerValkPitchedCladdingForRoofType(roofTypeName)
    if (claddingType && isVanDerValkPitchedCladdingType(claddingType)) {
      ensureVanDerValkPitchedCladdingOptionsForType(system, claddingType as VanDerValkPitchedCladdingType)
    }
  }

  applyVanDerValkDlubalUKFieldsFromCacheIfNeeded(system)
  void requestAutoFetchDlubalUKDataIfNeeded().then(() => {
    applyVanDerValkDlubalUKFieldsFromCacheIfNeeded(system)
    applyVanDerValkDlubalFetchFallbacksIfUnset()
  })

  requestVanDerValkSuitabilityFetchIfNeeded(system, {
    allowEdit: true,
    autoSelectDefault: true,
  })
}
